/**
 * @fileoverview Lookup helpers for the BRAND_REGISTRY (ESM).
 *
 * Mirrors `../getBrand.js` (CJS). `getBrand(appKey)` is the 99% caller
 * path — throws synchronously on unknown input (Gen-0 fail-loud).
 *
 * @module @puffin-core/brand-assets/getBrand
 */

import { BRAND_REGISTRY } from './registry.js';
import { APP_KEYS } from './constants.js';

/**
 * Resolve a brand entry by appKey.
 *
 * @param {string} appKey - One of {@link APP_KEYS}.
 * @returns {Readonly<object>} The frozen brand entry.
 * @throws {Error} If `appKey` is not a non-empty string in {@link APP_KEYS}.
 */
export function getBrand(appKey) {
  if (typeof appKey !== 'string' || appKey.length === 0) {
    throw new Error(
      `getBrand: appKey must be a non-empty string. Received: ${typeof appKey} (${String(appKey)}). ` +
        `Valid keys: ${APP_KEYS.join(', ')}`,
    );
  }
  if (!Object.prototype.hasOwnProperty.call(BRAND_REGISTRY, appKey)) {
    throw new Error(`getBrand: unknown appKey "${appKey}". Valid keys: ${APP_KEYS.join(', ')}`);
  }
  return BRAND_REGISTRY[appKey];
}

/**
 * Return all brand entries in canonical APP_KEYS order.
 *
 * @returns {Readonly<object>[]}
 */
export function getAllBrands() {
  return APP_KEYS.map((key) => BRAND_REGISTRY[key]);
}

/**
 * Predicate version of {@link getBrand}. Never throws.
 *
 * @param {unknown} appKey
 * @returns {boolean}
 */
export function hasBrand(appKey) {
  if (typeof appKey !== 'string' || appKey.length === 0) {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(BRAND_REGISTRY, appKey);
}

/**
 * The review platforms a brand invites EVERY customer to (LINKABLE-ASSETS-001 E3). Mirrors
 * `../getBrand.js` (CJS): the brand's frozen `reviewTargets`, empty until Q-LA-6 names them.
 *
 * @param {string} appKey - One of {@link APP_KEYS}.
 * @returns {ReadonlyArray<{platform: string, url: string}>}
 * @throws {Error} From {@link getBrand} on an unknown key.
 */
export function reviewTargetsOf(appKey) {
  return getBrand(appKey).reviewTargets;
}

/** Mirrors `../getBrand.js` (CJS): ParcelPuffin's permissions are `pos:*`, its brand is `parcel`. */
const BRAND_KEY_BY_APP_KEY = Object.freeze({ pos: 'parcel' });

/**
 * The brand of an app named by its PERMISSION-namespace key. Mirrors `../getBrand.js` (CJS).
 *
 * @param {string} appKey - A permission-namespace app key (`pos`, `planner`, …) or a brand key.
 * @returns {string|null} The brand key, or `null` when the key names no brand (`ecosystem`).
 * @throws {Error} If `appKey` is not a non-empty string.
 */
export function brandKeyForAppKey(appKey) {
  if (typeof appKey !== 'string' || appKey.length === 0) {
    throw new Error(
      `brandKeyForAppKey: appKey must be a non-empty string. Received: ${typeof appKey} (${String(appKey)})`,
    );
  }
  const brandKey = Object.prototype.hasOwnProperty.call(BRAND_KEY_BY_APP_KEY, appKey)
    ? BRAND_KEY_BY_APP_KEY[appKey]
    : appKey;
  return hasBrand(brandKey) ? brandKey : null;
}
